import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { withTheme } from 'styled-components';
import constants from 'constants';
import {
  ArtStage,
  ArtScale,
  ThumbFrame,
  Thumb,
  Sheen,
  ArtLayer,
  Tray,
  Chevron,
  DriftDot,
  QueueTrack,
  Pill,
  CubeStage,
  Cube,
  CubeEdges,
  CheckMark,
  CheckPath,
  SuccessRing,
  ErrorMark,
} from './styles';

const { JOB_PHASES } = constants;

const DOTS = [
  { offset: -1.25, delay: 0 },
  { offset: 0, delay: 300 },
  { offset: 1.25, delay: 600 },
];

const PILLS = [ 0, 800, 1600 ];

const WaitingArt = props => {
  const { themeProps } = props;

  return (
    <Tray {...themeProps}>
      <Chevron {...themeProps}>
        <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
          <path
            d='M12 19V5M12 5L5 12M12 5L19 12'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </Chevron>

      {DOTS.map(dot => (
        <DriftDot
          key={dot.delay}
          {...themeProps}
          $offset={dot.offset}
          $delay={dot.delay}
        />
      ))}
    </Tray>
  );
};

const QueuedArt = props => {
  const { themeProps } = props;

  return (
    <QueueTrack {...themeProps}>
      {PILLS.map((delay, index) => (
        <Pill
          key={delay}
          {...themeProps}
          $delay={delay}
          $leading={index === 0}
        />
      ))}
    </QueueTrack>
  );
};

// A mesh being assembled: the edges draw themselves in while the cube turns.
const ProcessingArt = props => {
  const { themeProps } = props;

  return (
    <CubeStage {...themeProps}>
      <Cube {...themeProps} width='96' height='96' viewBox='0 0 100 100' fill='none'>
        <CubeEdges
          stroke='currentColor'
          strokeWidth='2'
          strokeLinecap='round'
          strokeLinejoin='round'
        >
          <path d='M25 35H70V80H25V35Z' />
          <path d='M35 25H80V70H35V25Z' />
          <path d='M25 35L35 25' />
          <path d='M70 35L80 25' />
          <path d='M70 80L80 70' />
          <path d='M25 80L35 70' />
        </CubeEdges>
      </Cube>
    </CubeStage>
  );
};

const ReadyArt = props => {
  const { themeProps } = props;

  return (
    <>
      <SuccessRing {...themeProps} />

      <CheckMark {...themeProps} width='58' height='58' viewBox='0 0 58 52' fill='none'>
        <CheckPath
          {...themeProps}
          d='M14 27L24 37L44 15'
          stroke='currentColor'
          strokeWidth='4'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </CheckMark>
    </>
  );
};

const TerminalArt = props => {
  const { themeProps } = props;

  return (
    <ErrorMark {...themeProps}>
      <svg width='28' height='28' viewBox='0 0 24 24' fill='none'>
        <path
          d='M12 7V13M12 17H12.01'
          stroke='currentColor'
          strokeWidth='2.5'
          strokeLinecap='round'
        />
      </svg>
    </ErrorMark>
  );
};

const getArt = (phase, themeProps) => {
  switch (phase) {
    case JOB_PHASES.WAITING_FOR_FILE:
      return <WaitingArt themeProps={themeProps} />;

    case JOB_PHASES.QUEUED:
      return <QueuedArt themeProps={themeProps} />;

    case JOB_PHASES.COMPLETED:
    case JOB_PHASES.READY:
      return <ReadyArt themeProps={themeProps} />;

    case JOB_PHASES.FAILED:
    case JOB_PHASES.NOT_FOUND:
    case JOB_PHASES.UNAVAILABLE:
      return <TerminalArt themeProps={themeProps} />;

    default:
      return <ProcessingArt themeProps={themeProps} />;
  }
};

const StateArt = withTheme(props => {
  const { theme, phase, thumbnailURL } = props;
  const appStore = useSelector(state => state.app);
  const selectedTheme = appStore.theme;
  const themeProps = { theme, $selectedTheme: selectedTheme };
  const [ thumbnailFailed, setThumbnailFailed ] = useState(false);

  // The .rpc.png only exists once the RPC format has been built, so a URL that
  // 404s early may start working later in the same session.
  useEffect(() => {
    setThumbnailFailed(false);
  }, [ thumbnailURL ]);

  const showThumbnail = Boolean(thumbnailURL) && !thumbnailFailed;

  return (
    <ArtStage {...themeProps}>
      <ArtScale {...themeProps}>
        <ThumbFrame {...themeProps}>
          {showThumbnail && (
            <Thumb
              {...themeProps}
              src={thumbnailURL}
              alt=''
              onError={() => setThumbnailFailed(true)}
            />
          )}

          <Sheen {...themeProps} />
        </ThumbFrame>

        <ArtLayer {...themeProps}>
          {getArt(phase, themeProps)}
        </ArtLayer>
      </ArtScale>
    </ArtStage>
  );
});

export { StateArt };
